/**
 * 居民“大约几点回到驿站 / 到家”的共用说法（公开居民 / 领养候选上的 back_at，2026-09-25）。
 * 迎接确认框（adoptFlow.tsx）与答题分身的入住等待页、入住后的到家时刻都用它，几处说法必须一致，时间只在这里格式化：
 * formatBackAt 只给时间那一段；backAtPhrase 给“大约 19:22 回到驿站”或“大约 19:22 到家”这一截，句子的其余部分由调用方写。
 * - 没有、没传、认不出、已经过点 → null（说不准就不说，调用方用不带时间的那句）；
 * - 与 now 同一天 → “19:22”；第二天 → “明天 08:10”；更远 → “9月27日 08:10”（日期与时间之间是不断行空格，不会被拆到两行）；
 * - 按设备本地时区；分钟向上取整（19:21:01 说成 19:22），只会说晚、不会说得比实际早；先取整再看是哪一天（23:59:30 → 明天 00:00）。
 * now 由调用方传进来（页面传校准过的服务器时间 new Date(serverNow())），这里不读时钟。
 */
const NBSP = "\u00a0";
const MINUTE_MS = 60_000;

const twoDigits = (value: number) => String(value).padStart(2, "0");

/**
 * 后端给到微秒（例如 2026-09-25T11:22:36.201484Z），ES 只规定三位毫秒，更多位数认不认由各引擎自定：先截成三位再交给 Date；
 * 截掉的位数里有非零就进一毫秒，认出来的时刻不早于实际。
 */
function parseInstant(value: string): number {
  const match = value.match(/^(.+T[0-9]{2}:[0-9]{2}:[0-9]{2})[.]([0-9]+)(.*)$/);
  if (!match) return new Date(value).getTime();
  const [, head, digits, zone] = match;
  const ms = new Date(`${head}.${digits.slice(0, 3).padEnd(3, "0")}${zone}`).getTime();
  return /[1-9]/.test(digits.slice(3)) ? ms + 1 : ms;
}

export function formatBackAt(backAt: string | null | undefined, now: Date): string | null {
  if (!backAt) return null;
  const at = parseInstant(backAt);
  const nowMs = now.getTime();
  if (Number.isNaN(at) || Number.isNaN(nowMs) || at <= nowMs) return null;
  const shown = new Date(Math.ceil(at / MINUTE_MS) * MINUTE_MS);
  const time = `${twoDigits(shown.getHours())}:${twoDigits(shown.getMinutes())}`;
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const dayAfter = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 2);
  if (shown < tomorrow) return time;
  if (shown < dayAfter) return `明天${NBSP}${time}`;
  return `${shown.getMonth() + 1}月${shown.getDate()}日${NBSP}${time}`;
}

/** 这一趟回到哪：station = 回到驿站（默认；领养前、入住仍要等 TA 回驿站）；home = 到家（入住后这一趟直接回新家、入住后的到家时刻）。 */
export type BackAtPlace = "station" | "home";

/**
 * “大约 19:22 回到驿站”“大约明天 08:10 回到驿站”“大约 9月27日 08:10 回到驿站”；place 为 home 时同一套写成“大约 19:22 到家”。
 * 时间以数字开头时“大约”后面空一格。说不准（formatBackAt 为 null）就是 null，调用方用不带时间的那句。
 */
export function backAtPhrase(backAt: string | null | undefined, now: Date, place: BackAtPlace = "station"): string | null {
  const at = formatBackAt(backAt, now);
  if (!at) return null;
  return `大约${/^[0-9]/.test(at) ? " " : ""}${at} ${place === "home" ? "到家" : "回到驿站"}`;
}
